import React, { Component, PropTypes } from 'react';
import { observer, PropTypes as MobxPropTypes } from 'mobx-react';

// data dependencies
import { draggableClass, TileTypes } from 'logic/data/Izenda.ReportPartKPIConstants.js';

// share dependencies
import MenuContext from 'share/components/controls/Izenda.MenuContext.js';
import Localizer from 'share/localization/Izenda.Localizer.js';
import ReportPartUtils from 'share/utils/Izenda.ReportPartUtils';
// local dependencies
import KPIImageContainer from './Izenda.KPIImageContainer';
import KPITextContainer from './Izenda.KPITextContainer';
import KPIMetricContainer from './Izenda.KPIMetricContainer';
import ReportDesignerWidget from 'share/components/hoc/Izenda.ReportDesignerWidget';

/**
 * @class KPIGridItemContainer
 * @description renders a container for KPI tiles that wraps them in the ReportDesignerWidget
 * and allows us to set the active tile on click
 */
@observer
class KPIGridItemContainer extends Component {
    constructor(props) {
        super(props);

        this.state = {
            showContextMenu: false
        };
    }

    /**
     * @method componentDidMount
     * @description on mount, call ReportDesignerWidget onClick method, which switches
     * the properties panel to Tile Properties then set the active tile
     */
    componentDidMount() {
        const {
            isPreview,
            onClick,
            reportPart: {
                reportPartContent
            },
            tile
        } = this.props;

        if (isPreview) {
            onClick(this.props).then(valid => {
                valid && reportPartContent.setActiveTile(tile);
            });
        }
    }

    /**
     * @method onContainerClick
     * @description set the active tile when the tile is clicked and switch properties panel to tile properties
     * Note: We have to switch the properties editor so that the min props for grid dimensions properties reload properly
     */
    onContainerClick = () => {
      const {
        props,
        props: { isPreview, tile }
      } = this;
      if (isPreview) {
          this.props.reportPart.reportPartContent.setActiveTile(tile);
          this.props.onClick(props);
      }
      !isPreview && this.openCustomUrl(tile);
    }

    /**
     * @method tile
     * @description open new  url window which associate with tile
     */
    openCustomUrl(tile) {
      const { properties: { customURL }} = tile;
      customURL.url != '' && ReportPartUtils.openCustomURL(this.props, customURL, [], undefined, true);
    }

    /**
     * @method deleteTile
     * @description delete active tile and reset fields if tile is a metric tile
     */
    deleteTile = () => {
        const {
            reportPart,
            reportPart: {
                reportPartContent
            },
            tile
        } = this.props;

        reportPartContent.deleteActiveTile();
        if (tile.tileType === TileTypes.metric.id) {
            reportPart.clearContainer(reportPartContent.values);
        }
    }

    /**
     * @method renderContextMenu
     * @description render the context menu that allows users to delete tiles
     */
    renderContextMenu() {
        return (
            <div className="izenda-ReportPartHeader_ContextMenu">
                <ul className="izenda-ContextMenu-list">
                    <button
                        className={'btn btn-default'}
                        onClick={this.deleteTile}
                    >
                        <i className="icon-cancel" />
                        <span>{Localizer.getText('COMMON_LABEL_DELETE')}</span>
                    </button>
                </ul>
            </div>
        );
    }

    /**
     * @method renderContainerContent
     * @description render specific tile container based on tile type
     */
    renderContainerContent = () => {
        const {
            isPreview,
            tile
        } = this.props;

        switch (tile.tileType) {
            case TileTypes.metric.id:
                return <KPIMetricContainer {...this.props} />;
            case TileTypes.text.id:
                return (
                    <KPITextContainer
                        id={tile.id}
                        tile={tile}
                        isPreview={isPreview}
                        onActiveClick={this.onContainerClick}
                    />
                );
            case TileTypes.image.id:
                return <KPIImageContainer
                            src={tile.properties.image}
                            isPreview={isPreview}
                            properties={tile.properties}
                       />;
            default:
                return null;
        }
    }

    /**
     * @method render
     * @description render a container with an onClick event handler
     */
    render() {
        const {
            isPreview,
            reportPart: {
                reportPartContent
            },
            tile
        } = this.props;

        const activeClass = isPreview && reportPartContent.activeTile && reportPartContent.activeTile.id === tile.id ? 'iz-kpi__container--isActive' : '';

        return (
            <div
                className={`u_fullHeight ${draggableClass} ${activeClass}`}
                onMouseDown={this.onContainerClick}
            >
                <MenuContext
                    classes='u_fullHeight'
                    id={tile.id}
                    menuComponent={this.renderContextMenu()}
                >
                    {this.renderContainerContent()}
                </MenuContext>
            </div>
        );
    }
}

KPIGridItemContainer.propTypes = {
    isPreview: PropTypes.bool,
    onClick: PropTypes.func.isRequired,
    propsEditors: PropTypes.arrayOf(PropTypes.object).isRequired,
    reportPart: MobxPropTypes.observableObject.isRequired,
    tile: MobxPropTypes.observableObject.isRequired,
};

export default ReportDesignerWidget.wrapper(KPIGridItemContainer);



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartKPI/Izenda.KPIGridItemContainer.jsx